import { UserPlus } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrentWorkspace, useMembers } from "~/api/queries";
import { ABILITY } from "~/api/types";
import { InviteDialog } from "~/features/peopleAdmin/InviteDialog";
import { MemberRowMenu } from "~/features/peopleAdmin/MemberRowMenu";
import { Avatar } from "~/foundation/components/Avatar";
import { Button } from "~/foundation/components/Button";
import { Empty } from "~/foundation/components/Empty";
import { Spinner } from "~/foundation/components/Spinner";
import { formatRelativeTime } from "~/foundation/utils/time";

export function Members() {
  const { t, i18n } = useTranslation();
  const { data: members, isLoading } = useMembers();
  const { data: workspace } = useCurrentWorkspace();
  const [inviting, setInviting] = useState(false);

  // E-8: a member's own `abilities` ride an all-true `BuildPolicies` stub — the workspace
  // ability is the only trustworthy gate, never a row's `abilities.update`.
  const canManage = workspace?.abilities[ABILITY.workspaceManageMembers] === true;

  if (isLoading) {
    return <Spinner label={t("Loading")} />;
  }

  if (!members?.length) {
    return <Empty message={t("No members yet")} />;
  }

  return (
    <div className="flex flex-col gap-4">
      {canManage ? (
        <div className="flex justify-end">
          <Button variant="primary" icon={UserPlus} onClick={() => setInviting(true)}>
            {t("Invite people")}
          </Button>
        </div>
      ) : null}

      <ul className="flex flex-col rounded-md border border-divider">
        {members.map((member) => (
          <li
            key={member.id}
            className="flex items-center gap-3 border-b border-divider px-3 py-2.5 last:border-b-0"
          >
            <Avatar name={member.name} src={member.avatarUrl} size={28} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-body text-text">{member.name}</p>
              <p className="truncate text-small text-text-tertiary">{member.email}</p>
            </div>
            {member.suspended ? (
              <span className="rounded-sm bg-bg-secondary px-2 py-0.5 text-small text-text-tertiary">
                {t("Suspended")}
              </span>
            ) : null}
            <span className="w-20 text-small text-text-secondary capitalize">{member.role}</span>
            <span className="hidden w-28 text-small text-text-tertiary sm:inline">
              {member.lastActiveAt ? formatRelativeTime(member.lastActiveAt, i18n.language) : "—"}
            </span>
            {canManage ? <MemberRowMenu member={member} /> : null}
          </li>
        ))}
      </ul>

      {inviting ? <InviteDialog onClose={() => setInviting(false)} /> : null}
    </div>
  );
}
